import type {
  AccessDeactivationQueueItem,
  AccessQueueItemPublic,
  AccessQueueType,
  SessionUser,
} from '@accounts/shared';
import { ACCESS_QUEUE_TYPES } from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getAccessQueue } from '../../../../lib/api';
import { accessQueueScopeOf, queryKeys } from '../../../../lib/queryKeys';

/** HR or IT acting with limited-manager scope; administrator rights always win. */
export function isLimitedManager(user: SessionUser): boolean {
  return !user.isAdmin && (user.isHr || user.isIt);
}

/** A stable selection key: a request id and a record id can never collide across types. */
export function itemKey(item: AccessQueueItemPublic): string {
  return `${item.type}:${item.id}`;
}

export interface RoleQueue {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Oldest first, exactly the server's order, cut to what this actor works on. */
  items: readonly AccessQueueItemPublic[];
  /** Administrator-only, read-only work waiting for consuming applications to confirm. */
  deactivations: readonly AccessDeactivationQueueItem[];
  counts: Record<AccessQueueType, number>;
  deactivationCounts: { open: number; overdue: number };
  overdueAfterDays: number | null;
  deprovisionOverdueHours: number | null;
}

/**
 * THE QUEUE AS THIS ACTOR SEES IT — one read for the Overview and the Queue, under the SAME
 * unfiltered key the hub's sub-nav reads, so the tiles, the lanes and the sub-nav count all
 * come from one set of rows. Filtering happens on those rows rather than in a second keyed
 * read: the Queue is one organisation's people × systems, and a filtered read beside the
 * unfiltered one would be a second answer to "what is waiting" that could disagree with it.
 *
 * HR SCOPE IS THE SERVER'S. For HR the server's Queue holds only items on catalog rows
 * flagged "HR can grant directly", counts included (SIR-7591), so HR's session never
 * receives a row outside its scope and nothing is cut in the browser. The key carries the
 * actor's scope, so an admin-wide answer already in the cache is never read back once the
 * same person's session turns HR-only.
 */
export function useRoleQueue(user: SessionUser): RoleQueue {
  const queue = useQuery({
    queryKey: queryKeys.accessQueue(accessQueueScopeOf(user), {}),
    queryFn: () => getAccessQueue(),
  });

  const items = useMemo(() => queue.data?.data ?? [], [queue.data]);

  // Counted from the rows in hand: the same rows the lanes draw, so a count can never
  // disagree with its own lane.
  const counts = useMemo(() => {
    const out = Object.fromEntries(ACCESS_QUEUE_TYPES.map((type) => [type, 0])) as Record<
      AccessQueueType,
      number
    >;
    for (const item of items) out[item.type] += 1;
    return out;
  }, [items]);

  return {
    isLoading: queue.isLoading,
    isError: queue.isError,
    refetch: () => {
      void queue.refetch();
    },
    items,
    deactivations: queue.data?.deactivations ?? [],
    counts,
    deactivationCounts: queue.data?.meta.deactivationCounts ?? { open: 0, overdue: 0 },
    overdueAfterDays: queue.data?.meta.overdueAfterDays ?? null,
    deprovisionOverdueHours: queue.data?.meta.deprovisionOverdueHours ?? null,
  };
}
